import { css, cx } from "@emotion/css";
import InputAdornment from "@mui/material/InputAdornment";
import type { AxiosError } from "axios";
import config from "config";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { pl } from "date-fns/locale";
import { useFormik } from "formik";
import { type ComponentType, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isIos } from "src/capacitor/appsHelpers";
import { UserAvatar } from "src/components/Default/Avatar";
import {
	OutlinedPrimaryButton,
	PrimaryButton,
	PrimaryLinkButton,
} from "src/components/Default/LekUI/Button";
import {
	FormTextField,
	TextField,
} from "src/components/Default/LekUI/TextField";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import { Loading as TextEditorLoading } from "src/components/TextEditor";
import isEmail from "src/helpers/isEmail";
import isPes from "src/helpers/isPes";
import { isLdek } from "src/helpers/services";
import {
	getUserInfo,
	getUserSpecialisationName,
	useActivePromocode,
	useEditUser,
} from "src/hooks/useUser";
import EditIcon from "src/images/Icons/newIcons/edit2.svg";
import SubscriptionIcon from "src/images/Icons/newIcons/subscription.svg";
import ProfileIcon from "src/images/Icons/newIcons/userProfile/account.svg";
import PasswordIcon from "src/images/Icons/newIcons/userProfile/password.svg";
import newApi from "src/newApi";
import * as Yup from "yup";

const Loading = TextEditorLoading as unknown as ComponentType<{
	isLoading: boolean;
	children: ReactNode;
}>;

interface SuccessBoxProps {
	open: boolean;
	onClose: () => void;
	children?: ReactNode;
	duration?: number;
}

const SuccessBox = ({
	open,
	onClose,
	children,
	duration = 3000,
}: SuccessBoxProps) => {
	const { t } = useTranslation();

	useEffect(() => {
		if (!open) return;

		const timer = setTimeout(() => {
			onClose?.();
		}, duration);

		return () => clearTimeout(timer);
	}, [open, duration, onClose]);

	if (!open) return null;

	return (
		<div className="alert--correct p-8 border-radius-8 mb-16">
			{children || t("userAccount.successBox.defaultMessage")}
		</div>
	);
};

interface ContextContainerProps {
	children: ReactNode;
	isEdit: boolean;
	onSubmit?: () => void;
	onCancel?: () => void;
	loading?: boolean;
}

const ContextContainer = ({
	children,
	isEdit,
	onSubmit,
	onCancel,
	loading = false,
}: ContextContainerProps) => {
	const { t } = useTranslation();

	return (
		<Loading isLoading={loading}>
			<div className="p-16 border-medium-grey border-radius-18 p-relative">
				{children}
				{isEdit && (
					<div className="flex gap-8">
						<PrimaryButton
							className="mt-16"
							onClick={onSubmit}
							loading={loading}
						>
							{t("userAccount.contextContainer.saveButton")}
						</PrimaryButton>
						<OutlinedPrimaryButton className="mt-16" onClick={onCancel}>
							{t("userAccount.contextContainer.cancelButton")}
						</OutlinedPrimaryButton>
					</div>
				)}
			</div>
		</Loading>
	);
};

interface ItemProps {
	title: string;
	value: ReactNode;
}

const Item = ({ title, value }: ItemProps) => {
	return (
		<div className="flex flex-col">
			<span className="size-16 text-grey">{title}</span>
			<span className="size-18 first-letter-upper">{value}</span>
		</div>
	);
};

const PasswordSection = () => {
	const [isEdit, setIsEdit] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const { t } = useTranslation();
	const {
		submitForm,
		values,
		errors,
		setFieldValue,
		handleReset,
		isSubmitting,
	} = useFormik({
		initialValues: {
			newPassword: "",
			oldPassword: "",
			confirmNewPassword: "",
		},
		validateOnChange: false,
		onSubmit: async (
			{ oldPassword, newPassword },
			{ resetForm, setFieldError },
		) => {
			try {
				await newApi.updatePassword(oldPassword, newPassword);
				resetForm();
				setIsSuccess(true);
				setIsEdit(false);
				return true;
			} catch (err: unknown) {
				const error = err as AxiosError<{ user?: { email?: string } }>;
				if (error.response?.status === 409) {
					setFieldError("oldPassword", t("errors.wrongOldPassword"));
				}
			}
		},
		validationSchema: Yup.object().shape({
			oldPassword: Yup.string().required(t("errors.emptySingleField")),
			confirmNewPassword: Yup.string()
				.oneOf(
					[Yup.ref("newPassword"), null],
					t("errors.passwordsNotTheSameText"),
				)
				.min(8, t("errors.passwordToShort"))
				.required(t("errors.emptySingleField")),
			newPassword: Yup.string()
				.min(8, t("errors.passwordToShort"))
				.required(t("errors.emptySingleField")),
		}),
	});

	const { oldPassword, newPassword, confirmNewPassword } = values;

	return (
		<ContextContainer
			isEdit={isEdit}
			onSubmit={submitForm}
			onCancel={() => {
				setIsEdit(false);
				handleReset(undefined);
			}}
			loading={isSubmitting}
		>
			{!isEdit ? (
				<>
					<SuccessBox open={isSuccess} onClose={() => setIsSuccess(false)} />
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-16">
							<PasswordIcon className="icon-big" />
							<h4 className="size-20 m-0">
								{t("userAccount.passwordSection.sectionTitle")}
							</h4>
						</div>
						<PrimaryButton
							className="p-8 size-14"
							startIcon={<EditIcon className="icon-small mr-4" />}
							onClick={() => setIsEdit(!isEdit)}
						>
							<span className="size-14">
								{t("userAccount.passwordSection.editButton")}
							</span>
						</PrimaryButton>
					</div>
					<div className="flex flex-col gap-8 mt-32">
						<Item
							value="********"
							title={t("userAccount.passwordSection.sectionTitle")}
						/>
					</div>
				</>
			) : (
				<>
					<div className="flex items-center gap-16">
						<PasswordIcon className="icon-big" />
						<h4 className="size-20 m-0">
							{t("userAccount.passwordSection.changePasswordTitle")}
						</h4>
					</div>
					<div
						className={cx(
							"flex flex-col gap-24 mt-32",
							css`
                max-width: min(500px, 100%);
              `,
						)}
					>
						<TextField
							label={errors.oldPassword || t("auth.form.oldPassword")}
							variant="outlined"
							value={oldPassword}
							fullWidth
							type="password"
							onChange={(e) => setFieldValue("oldPassword", e.target.value)}
							error={errors.oldPassword}
						/>
						<TextField
							label={errors.newPassword || t("auth.form.newPassword")}
							variant="outlined"
							value={newPassword}
							fullWidth
							type="password"
							onChange={(e) => setFieldValue("newPassword", e.target.value)}
							error={errors.newPassword}
						/>
						<TextField
							label={errors.confirmNewPassword || t("auth.form.passwordRepeat")}
							variant="outlined"
							value={confirmNewPassword}
							fullWidth
							type="password"
							onChange={(e) =>
								setFieldValue("confirmNewPassword", e.target.value)
							}
							error={errors.confirmNewPassword}
						/>
					</div>
				</>
			)}
		</ContextContainer>
	);
};

const SubscriptionSection = () => {
	const { t } = useTranslation();
	const userSpec = getUserSpecialisationName();
	const userInfo = getUserInfo();
	const { mutateAsync: activate } = useActivePromocode() as unknown as {
		mutateAsync: (params: Record<string, unknown>) => Promise<unknown>;
	};
	const accessEndDate = userInfo.accessEndDate;
	const endDate = accessEndDate
		? format(parseISO(accessEndDate), "dd MMMM yyyy", { locale: pl })
		: t(
				"userAccount.subscriptionSection.noSubscription",
				"Brak aktywnej subskrypcji",
			);
	const daysLeft = accessEndDate
		? differenceInCalendarDays(parseISO(accessEndDate), new Date())
		: -1;
	const isLastWeek = daysLeft >= 0 && daysLeft <= 7;

	const { submitForm, resetForm, values, errors, setFieldValue } = useFormik({
		initialValues: {
			promocode: "",
		},
		validateOnChange: true,
		onSubmit: async (values, { setFieldError }) => {
			try {
				await activate(values);
				resetForm();
				return true;
			} catch (err: unknown) {
				const error = err as AxiosError<{ user?: { email?: string } }>;
				if (error.response?.status === 404) {
					setFieldError("promocode", t("errors.wrongPromocode"));
				} else {
					setFieldError("promocode", t("errors.somethingWentWrong"));
				}
			}
		},
		validationSchema: Yup.object().shape({
			promocode: Yup.string().required(t("errors.emptySingleField")),
		}),
	});

	const goToPricing = () => {
		if (isPes()) {
			const specId = userInfo.selectedSpecialisation?.id;
			location.href = specId
				? `${config.urls.pricingPes}${specId}`
				: config.urls.pricingPes;
			return;
		}
		location.href = config.urls.pricing;
	};

	const subscriptionInfo = isIos() ? (
		endDate
	) : (
		<div>
			<span>{endDate}</span>
			<PrimaryLinkButton className="size-18" onClick={goToPricing}>
				{t("userAccount.subscriptionSection.extendButton")}
			</PrimaryLinkButton>
		</div>
	);

	return (
		<ContextContainer isEdit={false}>
			<div className="flex items-center gap-16">
				<SubscriptionIcon className="icon-big" />
				<h4 className="size-20 m-0">
					{t("userAccount.subscriptionSection.sectionTitle")}
				</h4>
			</div>
			<div
				className={cx(
					"flex flex-col gap-24 mt-32",
					css`
            max-width: min(500px, 100%);
          `,
				)}
			>
				{isLastWeek && (
					<div className="alert--warning p-8 border-radius-8">
						{t("userAccount.subscriptionSection.subscriptionWarning")}
					</div>
				)}
				{isPes() && (
					<Item
						value={userSpec}
						title={t("userAccount.subscriptionSection.specialisationLabel")}
					/>
				)}
				{!isPes() && (
					<Item
						value={
							isLdek()
								? t("userAccount.subscriptionSection.accessTypeLdek")
								: t("userAccount.subscriptionSection.accessTypeLek")
						}
						title={t("userAccount.subscriptionSection.accessTypeTitle")}
					/>
				)}
				<Item
					value={subscriptionInfo}
					title={t("userAccount.subscriptionSection.expiryDateLabel")}
				/>
				<FormTextField
					className="pr-0"
					margin="none"
					label={t("modals.activatePromocode.label")}
					value={values.promocode}
					error={errors.promocode}
					onChange={(e) => setFieldValue("promocode", e.target.value)}
					slotProps={{
						input: {
							endAdornment: (
								<InputAdornment position="end">
									<PrimaryButton
										className={css`
                    height: 48px;
                    margin-right: -8px;
                  `}
										onClick={submitForm}
									>
										{t("userAccount.subscriptionSection.activateButton")}
									</PrimaryButton>
								</InputAdornment>
							),
						},
					}}
				/>
			</div>
		</ContextContainer>
	);
};

const AccountSection = () => {
	const { openModal } = useModals();
	const { t } = useTranslation();
	const userInfo = getUserInfo();
	const { mutateAsync } = useEditUser() as unknown as {
		mutateAsync: (params: Record<string, unknown>) => Promise<unknown>;
	};
	const [isEdit, setIsEdit] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const { submitForm, resetForm, values, errors, setFieldValue, isSubmitting } =
		useFormik({
			initialValues: {
				email: userInfo.email,
			},
			validateOnChange: true,
			onSubmit: async (values, { setFieldError }) => {
				try {
					if (!isEmail(values.email)) {
						setFieldError("email", t("errors.wrongEmailFormat"));
						return;
					}

					await mutateAsync(values);
					setIsSuccess(true);
					setIsEdit(false);
					return true;
				} catch (err: unknown) {
					const error = err as AxiosError<{ user?: { email?: string } }>;
					if (
						error.response?.status === 409 &&
						error.response?.data?.user?.email
					) {
						setFieldError("email", t("errors.emailExists"));
					} else {
						setFieldError("email", t("errors.somethingWentWrong"));
					}
				}
			},
			validationSchema: Yup.object().shape({
				email: Yup.string().required(t("errors.emptySingleField")),
			}),
		});

	return (
		<ContextContainer
			isEdit={isEdit}
			onSubmit={submitForm}
			onCancel={() => {
				resetForm();
				setIsEdit(false);
			}}
			loading={isSubmitting}
		>
			<div className="flex items-start justify-between">
				<div className="flex items-center gap-8">
					<UserAvatar onClick={() => openModal(ModalsNames.SetAvatarModal)} />
					<div className="profile--account-details">
						<span className="first-letter-upper size-16-light">
							{userInfo.username}
						</span>
						<span className="first-letter-upper size-16-light">
							{userInfo.email}
						</span>
					</div>
				</div>
				<PrimaryButton
					className="p-8 size-14"
					startIcon={<EditIcon className="icon-small mr-4" />}
					onClick={() => setIsEdit(!isEdit)}
				>
					<span className="size-14">
						{t("userAccount.accountSection.editButton")}
					</span>
				</PrimaryButton>
			</div>
			<div
				className={cx(
					"flex flex-col gap-24 mt-32",
					css`
            max-width: min(500px, 100%);
          `,
				)}
			>
				<SuccessBox open={isSuccess} onClose={() => setIsSuccess(false)} />
				{!isEdit ? (
					<>
						<Item
							value={userInfo.username}
							title={t("userAccount.accountSection.usernameLabel")}
						/>
						<Item
							value={userInfo.email}
							title={t("userAccount.accountSection.emailLabel")}
						/>
					</>
				) : (
					<>
						<TextField
							label={t("userAccount.accountSection.loginLabel")}
							variant="outlined"
							value={userInfo.username}
							fullWidth
							disabled
						/>
						<TextField
							label={t("userAccount.accountSection.emailLabel")}
							variant="outlined"
							value={values.email}
							fullWidth
							onChange={(e) => setFieldValue("email", e.target.value)}
							type="email"
							error={errors.email}
						/>
					</>
				)}
			</div>
		</ContextContainer>
	);
};

export const ProfileSections = () => (
	<div className="flex-1 gap-24 flex flex-col">
		<AccountSection />
		<PasswordSection />
		<SubscriptionSection />
	</div>
);

function Profile() {
	const { t } = useTranslation();

	return (
		<div className="profile--container">
			<div className="flex gap-16 flex-col">
				<div className="flex items-center gap-16">
					<ProfileIcon className="icon-big" />
					<h4 className="size-24">{t("userAccount.profile.mainTitle")}</h4>
				</div>
				<ProfileSections />
			</div>
		</div>
	);
}

export default Profile;
